/**
 * Contact note defaults and the rules checked before the counselor signs
 * (mirrors contact_note.go on the server).
 */
import {
  PRP_INTERVENTION_TYPES,
  PRP_IRP_DOMAINS,
  PRP_MODES_OF_SERVICE,
  PRP_PARTICIPATION,
  type PrpContactNoteAIDraft,
  type PrpContactNoteContent,
} from "@/api/prp.api";
import { parseOnto } from "./use-draft";

export const MAX_CONTACT_MINUTES = 8 * 60;

export function emptyContactNote(irpId = ""): PrpContactNoteContent {
  return {
    start_time: "",
    end_time: "",
    setting: "",
    telehealth_method: "",
    location: "",
    reason: "",
    interventions: "",
    participation: "",
    irp_id: irpId,
    goals: [],
    mode_of_service: [],
    domains: [],
    intervention_types: [],
    transportation_to: "",
  };
}

export function parseContactNote(raw?: string): PrpContactNoteContent {
  const c = parseOnto(raw, emptyContactNote());
  return {
    ...c,
    goals: c.goals ?? [],
    mode_of_service: c.mode_of_service ?? [],
    domains: c.domains ?? [],
    intervention_types: c.intervention_types ?? [],
  };
}

/** Minutes between HH:MM times, or null when missing / not after start. */
export function contactMinutes(start: string, end: string): number | null {
  const m = /^(\d{2}):(\d{2})$/;
  const a = m.exec(start);
  const b = m.exec(end);
  if (!a || !b) return null;
  const mins = Number(b[1]) * 60 + Number(b[2]) - (Number(a[1]) * 60 + Number(a[2]));
  return mins > 0 ? mins : null;
}

export function fmtDuration(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h ? `${h} hr ${m} min` : `${m} min`;
}

const blank = (s?: string) => !s || s.trim() === "";
const keys = (list: { key: string }[]) => new Set(list.map((x) => x.key));
const MODES = keys(PRP_MODES_OF_SERVICE);
const DOMAINS = keys(PRP_IRP_DOMAINS);
const INTERVENTIONS = keys(PRP_INTERVENTION_TYPES);
const PARTICIPATION = keys(PRP_PARTICIPATION);

export function contactNoteProblems(c: PrpContactNoteContent): string[] {
  const p: string[] = [];
  const mins = contactMinutes(c.start_time, c.end_time);
  if (mins === null) p.push("Start and end time (end after start)");
  else if (mins > MAX_CONTACT_MINUTES) p.push("Check the start and end times (over 8 hours)");
  if (!c.setting) p.push("On-site, off-site or telehealth");
  if (c.setting === "telehealth" && !c.telehealth_method) p.push("Telehealth: video or audio-only");
  if (blank(c.location)) p.push("Location");
  if (blank(c.reason)) p.push("Reason for visit");
  if (blank(c.interventions)) p.push("Activities and interventions");
  if (!PARTICIPATION.has(c.participation)) p.push("Level of participation");
  if (!c.goals.length) p.push("At least one IRP goal addressed");
  c.goals.forEach((g, i) => {
    if (g.progress < 1 || g.progress > 5) p.push(`Goal ${i + 1}: progress rating 1–5`);
    if (blank(g.progress_text)) p.push(`Goal ${i + 1}: progress toward goal`);
  });
  if (!c.mode_of_service.some((k) => MODES.has(k))) p.push("Mode of service");
  if (!c.domains.some((k) => DOMAINS.has(k))) p.push("Domain");
  if (!c.intervention_types.some((k) => INTERVENTIONS.has(k))) p.push("Type of intervention");
  if (c.intervention_types.includes("transportation") && blank(c.transportation_to))
    p.push("Transportation: where to");
  return p;
}

const union = (a: string[], b?: string[]) => [...a, ...(b ?? []).filter((x) => !a.includes(x))];

/**
 * Merges an AI draft into the note. Narrative and goal text are replaced;
 * visit details fill only empty fields; checkboxes are added, never removed.
 */
export function mergeContactDraft(
  c: PrpContactNoteContent,
  d: PrpContactNoteAIDraft,
): PrpContactNoteContent {
  const drafted = new Map((d.goals ?? []).map((g) => [g.goal_id, g]));
  const goals = c.goals.map((x) => {
    const g = drafted.get(x.goal_id);
    if (!g) return x;
    drafted.delete(x.goal_id);
    return {
      ...x,
      progress: g.progress || x.progress,
      progress_text: g.progress_text || x.progress_text,
    };
  });
  goals.push(...drafted.values());
  return {
    ...c,
    start_time: c.start_time || d.start_time || "",
    end_time: c.end_time || d.end_time || "",
    setting: c.setting || d.setting || "",
    telehealth_method: c.telehealth_method || d.telehealth_method || "",
    location: c.location || d.location || "",
    reason: d.reason || c.reason,
    interventions: d.interventions || c.interventions,
    participation: d.participation || c.participation,
    goals,
    mode_of_service: union(c.mode_of_service, d.mode_of_service),
    domains: union(c.domains, d.domains),
    intervention_types: union(c.intervention_types, d.intervention_types),
  };
}
